import { useEffect, useCallback, useState, useMemo } from "react";
import {
  LayerConfig,
  PingGoogleMapOverlaySelectedRegionType,
} from "../../index";
import { formatRegionAttribute } from "./formatRegionAttribute";

import "./MapsOverlayTooltip.scss";
import { Flex, TooltipCard } from "@repo/ping-design";

let _lastClickX = 0;
let _lastClickY = 0;

if (typeof document !== "undefined") {
  document.addEventListener("mousedown", (e: MouseEvent) => {
    _lastClickX = e.clientX;
    _lastClickY = e.clientY;
  });
}

const CURSOR_OFFSET = 16;
const TOOLTIP_WIDTH = 384;
const VIEWPORT_PADDING = 16;

const MapsOverlayTooltip = ({
  config,
  onClose,
  selected,
}: {
  config: LayerConfig;
  onClose: () => void;
  selected: PingGoogleMapOverlaySelectedRegionType;
}) => {
  const [clickPos, setClickPos] = useState(() => ({
    x: _lastClickX,
    y: _lastClickY,
  }));

  useEffect(() => {
    setClickPos({ x: _lastClickX, y: _lastClickY });
  }, [selected]);

  const tooltipStyle = useMemo(() => {
    let left = clickPos.x + CURSOR_OFFSET;
    let top = clickPos.y;

    if (left + TOOLTIP_WIDTH > window.innerWidth - VIEWPORT_PADDING) {
      left = clickPos.x - TOOLTIP_WIDTH - CURSOR_OFFSET;
    }
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;

    return { top, left };
  }, [clickPos]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="MapsOverlayTooltip" style={tooltipStyle}>
      <TooltipCard
        title={config.title}
        className="MapsOverlayTooltip__Card"
        onClose={onClose}
      >
        {config.attributes.map((attr) => {
          if (selected.properties?.[attr.field] === undefined) {
            return null;
          }

          const currField = selected.properties[attr.field];
          return (
            <Flex
              direction="row"
              align="center"
              justify="space-between"
              key={attr.field}
              className="MapsOverlayTooltip__Attribute"
              gap="4"
            >
              <div className="MapsOverlayTooltip__Label">{attr.label}</div>
              <div className="MapsOverlayTooltip__Value">
                {formatRegionAttribute(currField, attr)}
              </div>
            </Flex>
          );
        })}

        <Flex justify="center">
          <b>Press ESC or click outside to close</b>
        </Flex>
      </TooltipCard>
    </div>
  );
};

export default MapsOverlayTooltip;
